<template>
  <v-container fluid justify-start>

    <loading
      slot="activator"
      :active.sync="isSaving"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredKiosks"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      show-select
      item-key="id"
      class="elevation-2"
      >

      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>
      
        <!-- Header Toolbar -->
        <v-toolbar dense dark flat :color="toolbarColor">
          
          <app-back-button v-bind:color="toolbarColorDark" />

          <v-toolbar-title>Manage Override Kiosks</v-toolbar-title>

          <v-spacer />

          <v-toolbar-items>

            <!-- Save Button -->
            <v-btn @click="save()" :color="toolbarColorDark" :disabled="isSaving" dark class="elevation-0 mt-0 ml-2">
              <span>Save Changes</span>
            </v-btn>

          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">

          <!-- Selected -->
          <v-col cols="12" md="2">
            <v-checkbox
              v-model="selectedOnly"
              :color="appColor_Selected"
              :label="selectedLabel"
              class="mr-0"
            />
          </v-col>

          <!-- Search -->
          <v-col cols="12" md="10">
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
              :disabled="isLoading"
              clearable
              class="ml-0"
            />
          </v-col>
        </v-row>
      </template>


      <template v-slot:item.updateInstallDate="{ item }">
        <span class="text-left"><app-date :date="item.updateInstallDate" :format="'MMM-DD-YYYY'"/></span>
      </template>

      <template v-slot:item.mediaSyncDate="{ item }">
        <span class="mr-4">
          <v-icon v-if="isOnline(item.mediaSyncDate)" :color="appColor_Selected">done</v-icon>
          <v-icon v-else :color="'grey lighten-2'">cancel</v-icon>
        </span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/></span>
      </template>

    </v-data-table>

  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { AppColor } from '@/config.js'
import AppBackButton from '@/components/App/AppBackButton'
import AppDate from '@/components/App/AppDate'

export default {
  name: 'UpdateOverrideAbbys',
  components: {
    AppBackButton,
    AppDate
  },
  data() {
    return {
      // colors
      toolbarColor: AppColor.Medium,
      toolbarColorDark: AppColor.Dark,
      appColor_Selected: AppColor.Selected,
      appColor_Progress: AppColor.Progress,

      isLoading: true,
      isSaving: false,
      selectedOnly: false,
      search: '',

      headers: [
        {
          text: 'S/N',
          value: 'serialNumber',
          align: 'left',
          width: '10%',
          class: 'pr-0'
        },
        {
          text: 'Description',
          value: 'description',
          align: 'left',
          width: '30%',
          class: 'pr-0'
        },
        {
          text: 'Facility',
          value: 'facilityName',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          text: 'Level',
          value: 'updateLevel',
          align: 'left',
          width: '7%',
          class: 'pr-0'
        },
        {
          text: 'Installed',
          value: 'updateInstallDate',
          align: 'left',
          width: '12%',
          class: 'pr-0'
        },
        {
          text: 'Online',
          value: 'mediaSyncDate',
          align: 'center',
          width: '7%'
        },        
        {
          text: 'Created',
          value: 'createDate',
          align: 'left',
          width: '12%',
          class: 'pr-0'
        }
      ],
      
      kiosks: [],
      selected: []
    }
  },
  computed: {
    noDataText () {
      if (this.isLoading) return 'One moment...'

      return this.kiosks.length === 0
        ? 'No kiosks found'
        : ''
    },
    filteredKiosks () {
      if (this.kiosks === null) return []

      const self = this
      return self.kiosks.filter((k) => {
        return ((k.refId !== null && self.selectedOnly) || !self.selectedOnly)
      })
    },
    selectedLabel () {
      const label = 'Selected Only'
      return `${label} (${this.selectedCount})`
    },
    selectedCount () {
      return this.selected.length
    }
  },
  methods: {
    loadData() {
      const self = this
      self.isLoading = true

      appService.getUpdateOverrideAbbys().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.kiosks = data.kiosks.map(k => { return {
              id: k.id,
              serialNumber: k.serialNumber,
              description: k.description,
              machineName: k.machineName,
              facilityName: k.facilityName,
              refId: k.refId,
              updateLevel: k.updateLevel,
              updateInstallDate: k.updateInstallDate,
              mediaSyncDate: k.mediaSyncDate,
              createDate: k.createDate
          }})
          self.selected = data.kiosks.filter(k => k.refId === 'update-override')
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },
   
    save () {
      const self = this
      const text = `Set for activation <b>${self.selectedCount}</b></br>` +
                   `Set for deactivation: <b>${self.kiosks.length - self.selectedCount}</b>`
      self.$confirm.show({
        title: 'Save Changes?',
        text: text,
        formColor: 'info',
        onConfirm: () => {
          self.isSaving = true
          const ids = self.selected.map(s => s.id)
          appService.activateUpdateOverrideAbbys(ids).then(data => {
            if (data.errorSeverity !== null) {
              self.handleError(data)
            }
            self.isSaving = false
          }).catch(e => { this.handleCriticalError(e) })
        }
      })
    },

    isOnline (mediaSyncDate) {
      const today = new Date()
      const dd = String(today.getDate()).padStart(2, '0')
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const yyyy = today.getFullYear()
      const todayDate = this.getDate(mm + '/' + dd + '/' + yyyy)
      const syncDate = this.getDate(mediaSyncDate)

      return this.getDate(syncDate) === todayDate
    },

    getDate (date) {
      if (!this.isNullOrUndefined(date)) {
        if (date.length > 0) {
          return this.$moment(date).format('MMM-DD-YYYY')
        }
      }
      return null
    },

    getTime (date) {
      if (date !== null) {
        if (date.length > 0) {
          return this.$moment(date).format('hh:mm:ss A')
        }
      }
      return null
    },

    customFilter (value, search, item) {
      const description = item.description ?? ''
      const facilityName = item.facilityName ?? ''
      const updateLevel = `${item.updateLevel}` ?? ''

      return (
        value != null &&
        search != null &&
        (item.serialNumber.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        description.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        facilityName.toLowerCase()
          .indexOf(search.toLowerCase()) !== -1 ||
        updateLevel
          .indexOf(search.toLowerCase()) !== -1)
      )
    },

    handleError (result) {
      this.$error.show({
        title:
          result.errorSeverity === 0 ? 'Validation Failed' : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  }
}
</script>
